import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin, Mountain, Heart, Award, Shield, Zap, Globe } from 'lucide-react';

/* ── Brand-colour social icons (inline SVG) ── */
const IconFacebook = () => (
  <svg viewBox="0 0 24 24" width="22" height="22" fill="#1877F2">
    <path d="M24 12.073C24 5.405 18.627 0 12 0S0 5.405 0 12.073C0 18.1 4.388 23.094 10.125 24v-8.437H7.078v-3.49h3.047v-2.66c0-3.025 1.792-4.697 4.533-4.697 1.312 0 2.686.236 2.686.236v2.971h-1.513c-1.491 0-1.956.93-1.956 1.885v2.265h3.328l-.532 3.49h-2.796V24C19.612 23.094 24 18.1 24 12.073z"/>
  </svg>
);

const IconInstagram = () => (
  <svg viewBox="0 0 24 24" width="22" height="22">
    <defs>
      <radialGradient id="ig-rg2" cx="30%" cy="107%" r="150%">
        <stop offset="0%"  stopColor="#fdf497"/>
        <stop offset="5%"  stopColor="#fdf497"/>
        <stop offset="45%" stopColor="#fd5949"/>
        <stop offset="60%" stopColor="#d6249f"/>
        <stop offset="90%" stopColor="#285AEB"/>
      </radialGradient>
    </defs>
    <rect width="24" height="24" rx="6" fill="url(#ig-rg2)"/>
    <path d="M12 7.2A4.8 4.8 0 1 0 12 16.8 4.8 4.8 0 0 0 12 7.2zm0 7.92A3.12 3.12 0 1 1 12 8.88a3.12 3.12 0 0 1 0 6.24z" fill="#fff"/>
    <circle cx="17.04" cy="6.96" r="1.12" fill="#fff"/>
    <path d="M16.8 2.4H7.2A4.8 4.8 0 0 0 2.4 7.2v9.6A4.8 4.8 0 0 0 7.2 21.6h9.6a4.8 4.8 0 0 0 4.8-4.8V7.2A4.8 4.8 0 0 0 16.8 2.4zm3.12 14.4a3.12 3.12 0 0 1-3.12 3.12H7.2a3.12 3.12 0 0 1-3.12-3.12V7.2A3.12 3.12 0 0 1 7.2 4.08h9.6A3.12 3.12 0 0 1 19.92 7.2v9.6z" fill="#fff"/>
  </svg>
);

const IconGmail = () => (
  <svg viewBox="0 0 24 24" width="22" height="22">
    <path d="M24 5.457v13.909c0 .904-.732 1.636-1.636 1.636h-3.819V11.73L12 16.64l-6.545-4.91v9.273H1.636A1.636 1.636 0 0 1 0 19.366V5.457c0-2.023 2.309-3.178 3.927-1.964L5.455 4.64 12 9.548l6.545-4.91 1.528-1.145C21.69 2.28 24 3.434 24 5.457z" fill="#EA4335"/>
    <path d="M0 5.457v13.909c0 .904.732 1.636 1.636 1.636h3.819V11.73L0 7.64V5.457z" fill="#C5221F"/>
    <path d="M24 5.457v2.183l-5.455 4.09V21h3.819A1.636 1.636 0 0 0 24 19.366V5.457z" fill="#C5221F"/>
    <path d="M0 7.64l5.455 4.09V4.64L3.927 3.493C2.309 2.28 0 3.434 0 5.457V7.64z" fill="#F6AD01"/>
    <path d="M24 7.64l-5.455 4.09V4.64l1.528-1.147C21.69 2.28 24 3.434 24 5.457V7.64z" fill="#F6AD01"/>
    <path d="M5.455 11.73L12 16.64l6.545-4.91V4.64L12 9.548 5.455 4.64v7.09z" fill="#4285F4"/>
  </svg>
);

const IconWhatsApp = () => (
  <svg viewBox="0 0 24 24" width="22" height="22" fill="none">
    <circle cx="12" cy="12" r="12" fill="#25D366"/>
    <path fill="#fff" d="M17.04 6.93A6.93 6.93 0 0 0 5.07 15.1L4 20l5.05-1.32a6.93 6.93 0 0 0 3.31.84h.01A6.93 6.93 0 0 0 17.04 6.93zm-4.67 10.66a5.75 5.75 0 0 1-2.93-.8l-.21-.12-2.18.57.58-2.13-.14-.22a5.76 5.76 0 1 1 4.88 2.7zm3.16-4.31c-.17-.09-.99-.49-1.14-.54-.15-.06-.26-.08-.38.08-.11.17-.44.54-.54.66-.1.11-.2.13-.37.04a4.7 4.7 0 0 1-1.38-.85 5.2 5.2 0 0 1-.96-1.19c-.1-.17-.01-.27.08-.36.08-.08.17-.2.26-.3.09-.1.12-.17.17-.28.06-.11.03-.21-.01-.3-.05-.08-.38-.91-.52-1.25-.14-.33-.28-.28-.38-.29h-.32c-.11 0-.3.04-.45.21-.16.17-.6.59-.6 1.43s.61 1.66.7 1.77c.08.12 1.2 1.83 2.9 2.57.41.17.72.28.97.36.41.13.78.11 1.07.07.33-.05.99-.4 1.13-.8.14-.38.14-.71.1-.78-.05-.07-.16-.11-.33-.2z"/>
  </svg>
);

const trekingRegions = [
  { name: "Everest Region",       href: "/destinations/everest"           },
  { name: "Annapurna Region",     href: "/destinations/annapurna"         },
  { name: "Langtang Valley",      href: "/destinations/langtang"          },
  { name: "Manaslu Circuit",      href: "/destinations/manaslu"           },
  { name: "Upper Mustang",        href: "/destinations/upper-mustang"     },
  { name: "Kangchenjunga",        href: "/destinations/kangchenjunga"     },
  { name: "Makalu Region",        href: "/destinations/makalu"            },
  { name: "Rolwaling Valley",     href: "/destinations/rolwaling"         },
  { name: "Dolpa Region",         href: "/destinations/dolpa"             },
];

const quickLinks = [
  { href: "/destinations",  label: "Destinations"   },
  { href: "/heli-tours",    label: "Helicopter Tours" },
  { href: "/services",      label: "Services"       },
  { href: "/blog",          label: "Blog"           },
  { href: "/about",         label: "About Us"       },
  { href: "/contact",       label: "Contact"        },
  { href: "/booking",       label: "Book Now"       },
];



const whyChooseUs = [
  { icon: Award,  title: "Expert Guides",      desc: "Licensed Sherpa & local guides" },
  { icon: Shield, title: "Safety First",       desc: "Comprehensive safety protocols" },
  { icon: Heart,  title: "5000+ Happy Trekkers", desc: "Proven track record since 2009" },
  { icon: Zap,    title: "24/7 Support",       desc: "Round-the-clock assistance" },
];

const socials = [
  { key: "facebook",  href: "https://www.facebook.com/profile.php?id=61573665246799", label: "Facebook"  },
  { key: "instagram", href: "https://www.instagram.com/trekmatenepal/?hl=en",         label: "Instagram" },
  { key: "gmail",     href: "mailto:trekmatenepal@gmail.com",                          label: "Gmail"     },
  { key: "whatsapp",  href: "https://wa.me/9779763474895",                             label: "WhatsApp"  },
];

const Footer: React.FC = () => {
  return (
    <footer className="bg-slate-900 text-slate-300">
      {/* Main grid */}
      <div className="max-w-7xl mx-auto px-4 lg:px-6 py-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10">

        {/* Brand & About */}
        <div>
          <img src="/logo192.png" alt="TrekMate Nepal" className="h-16 w-auto mb-4" />
          <p className="text-sm leading-relaxed text-slate-400 mb-6">
            Your trusted partner for Himalayan adventures since 2009. We craft unforgettable trekking experiences across Nepal&apos;s most breathtaking landscapes with expert guides and personalized service.
          </p>
          {/* Socials */}
          <div className="flex items-center gap-3">
            {socials.map(({ key, href, label }) => (
              <a
                key={key}
                href={href}
                target={href.startsWith('mailto') ? undefined : '_blank'}
                rel={href.startsWith('mailto') ? undefined : 'noopener noreferrer'}
                aria-label={label}
                className="hover:scale-110 transition-transform"
              >
                {key === 'facebook'  && <IconFacebook  />}
                {key === 'instagram' && <IconInstagram />}
                {key === 'gmail'     && <IconGmail     />}
                {key === 'whatsapp'  && <IconWhatsApp  />}
              </a>
            ))}
          </div>
        </div>

        {/* Trekking Regions */}
        <div>
          <h4 className="text-white font-bold mb-5 text-sm uppercase tracking-wider flex items-center gap-2">
            <Mountain className="h-4 w-4 text-orange-500" />
            Trekking Regions
          </h4>
          <ul className="space-y-2.5">
            {trekingRegions.map(({ href, name }) => (
              <li key={name}>
                <Link
                  to={href}
                  className="text-sm text-slate-400 hover:text-orange-400 transition-colors flex items-center gap-2 group"
                >
                  <span className="w-1 h-1 bg-orange-500 rounded-full group-hover:scale-150 transition-transform" />
                  {name}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        {/* Quick Links */}
        <div>
          <h4 className="text-white font-bold mb-5 text-sm uppercase tracking-wider flex items-center gap-2">
            <Globe className="h-4 w-4 text-orange-500" />
            Quick Links
          </h4>
          <ul className="space-y-2.5">
            {quickLinks.map(({ href, label }) => (
              <li key={label}>
                <Link
                  to={href}
                  className="text-sm text-slate-400 hover:text-orange-400 transition-colors flex items-center gap-2 group"
                >
                  <span className="w-1 h-1 bg-orange-500 rounded-full group-hover:scale-150 transition-transform" />
                  {label}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        {/* Contact */}
        <div>
          <h4 className="text-white font-bold mb-5 text-sm uppercase tracking-wider flex items-center gap-2">
            <Phone className="h-4 w-4 text-orange-500" />
            Contact Us
          </h4>
          <ul className="space-y-4">
            <li className="flex items-start gap-3">
              <MapPin className="h-4 w-4 text-orange-500 mt-0.5 shrink-0" />
              <div className="text-sm text-slate-400">
                <p className="font-semibold text-white mb-1">Office Address</p>
                <p>Thamel, Kathmandu</p>
                <p className="text-xs">Nepal 44600</p>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <Phone className="h-4 w-4 text-orange-500 mt-0.5 shrink-0" />
              <div className="text-sm text-slate-400">
                <p className="font-semibold text-white mb-1">Contact Number</p>
                <a href="tel:+9779763474895" className="hover:text-orange-400 transition-colors block">+977 9763474895</a>
                <p className="text-xs text-orange-400 mt-1">Available on WhatsApp</p>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <Mail className="h-4 w-4 text-orange-500 mt-0.5 shrink-0" />
              <div className="text-sm text-slate-400">
                <p className="font-semibold text-white mb-1">Email Address</p>
                <a href="mailto:trekmatenepal@gmail.com" className="hover:text-orange-400 transition-colors block">trekmatenepal@gmail.com</a>
                <p className="text-xs text-slate-400 mt-1">Quick response guaranteed</p>
              </div>
            </li>
          </ul>
        </div>
      </div>

      {/* Why Choose Us Section */}
      <div className="border-t border-slate-800">
        <div className="max-w-7xl mx-auto px-4 lg:px-6 py-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
          {whyChooseUs.map(({ icon: Icon, title, desc }) => (
            <div key={title} className="flex items-start gap-4 group">
              <div className="bg-orange-500/10 p-3 rounded-lg group-hover:bg-orange-500/20 transition-colors">
                <Icon className="h-5 w-5 text-orange-500" />
              </div>
              <div>
                <h5 className="text-white font-semibold text-sm mb-1">{title}</h5>
                <p className="text-slate-400 text-xs">{desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Bottom bar */}
      <div className="border-t border-slate-800">
        <div className="max-w-7xl mx-auto px-4 lg:px-6 py-6 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-slate-500">
          <p>© {new Date().getFullYear()} TrekMateNepal. All rights reserved.</p>
          <div className="flex items-center gap-6">
            <Link to="/about" className="hover:text-orange-400 transition-colors">About</Link>
            <Link to="/contact" className="hover:text-orange-400 transition-colors">Contact</Link>
            <Link to="/privacy" className="hover:text-orange-400 transition-colors">Privacy</Link>
            <Link to="/terms" className="hover:text-orange-400 transition-colors">Terms</Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
